import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactCommunicationPreferences } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactCommunicationPreferences;
  isDate?: boolean;
}

const COMMUNICATION_PREFS_FIELDS: FieldMapping[] = [
  { apiField: 'allowTextMessages', formField: 'allowTextMessages' },
  { apiField: 'doNotAllowBulkEmails', formField: 'doNotAllowBulkEmails' },
  { apiField: 'followEmailActivity', formField: 'followEmailActivity' },
  { apiField: 'disableWebTracking', formField: 'disableWebTracking' },
  { apiField: 'gdprOptout', formField: 'gdprOptout' },
  { apiField: 'gdprSuppress', formField: 'gdprSuppress' },
  { apiField: 'suppressCmlDripEmails', formField: 'suppressCmlDripEmails' },
  {
    apiField: 'temporarilySuppressAllEmails',
    formField: 'temporarilySuppressAllEmails'
  },
  {
    apiField: 'dateTempSuppressWasUnchecked',
    formField: 'dateTempSuppressUnchecked',
    isDate: true
  },
  {
    apiField: 'reasonUncheckedTempSuppress',
    formField: 'reasonForUncheckedTempSuppress'
  },
  {
    apiField: 'receiveInvestorCommunications',
    formField: 'receiveInvestorCommunications'
  },
  { apiField: 'unsubscribedDate', formField: 'unsubscribeDate', isDate: true },
  { apiField: 'unsubscribedBy', formField: 'unsubscribedBy' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactCommunicationPreferences,
  initial: ContactCommunicationPreferences
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : currentVal;
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        allowTextMessages
        doNotAllowBulkEmails
        followEmailActivity
        disableWebTracking
        gdprOptout
        gdprSuppress
        suppressCmlDripEmails
        temporarilySuppressAllEmails
        dateTempSuppressWasUnchecked
        reasonUncheckedTempSuppress
        receiveInvestorCommunications
        unsubscribedDate
        unsubscribedBy
      }
    }
  }
`;

export interface EditCommunicationPreferencesDialogProps {
  contactId: string;
  initialValue: ContactCommunicationPreferences;
  isOpen: boolean;
  onChange: (data: ContactCommunicationPreferences) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactCommunicationPreferences;
}

export function EditCommunicationPreferencesDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditCommunicationPreferencesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(
    COMMUNICATION_PREFS_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactCommunicationPreferences>(
    field: K,
    fieldValue: ContactCommunicationPreferences[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    // Validate: If "Temporarily Suppress All Emails" was checked and is now unchecked, reason is required
    const isUnchecking =
      initialValue.temporarilySuppressAllEmails &&
      !value.temporarilySuppressAllEmails;
    if (isUnchecking && !value.reasonForUncheckedTempSuppress.trim()) {
      toast.error(
        'Reason for unchecked Temp Suppress is required when Temporarily Suppress All Emails is unchecked'
      );
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save communication preferences');
      } else {
        toast.success('Communication preferences saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving communication preferences:', error);
      toast.error('Failed to save communication preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Communication Preferences"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.allowTextMessages}
          label="Allow Text Messages"
          onCheckedChange={allowTextMessages => {
            updateField('allowTextMessages', allowTextMessages);
          }}
        />
        <EditCheckboxField
          checked={value.doNotAllowBulkEmails}
          label="Do not allow Bulk Emails"
          onCheckedChange={doNotAllowBulkEmails => {
            updateField('doNotAllowBulkEmails', doNotAllowBulkEmails);
          }}
        />
        <EditCheckboxField
          checked={value.followEmailActivity}
          label="Follow Email Activity"
          onCheckedChange={followEmailActivity => {
            updateField('followEmailActivity', followEmailActivity);
          }}
        />
        <EditCheckboxField
          checked={value.disableWebTracking}
          label="Disable Web Tracking"
          onCheckedChange={disableWebTracking => {
            updateField('disableWebTracking', disableWebTracking);
          }}
        />
        <EditCheckboxField
          checked={value.gdprOptout}
          label="GDPR Optout"
          onCheckedChange={gdprOptout => {
            updateField('gdprOptout', gdprOptout);
          }}
        />
        <EditCheckboxField
          checked={value.gdprSuppress}
          label="GDPR Suppress"
          onCheckedChange={gdprSuppress => {
            updateField('gdprSuppress', gdprSuppress);
          }}
        />
        <EditCheckboxField
          checked={value.suppressCmlDripEmails}
          label="Suppress CML Drip Emails"
          onCheckedChange={suppressCmlDripEmails => {
            updateField('suppressCmlDripEmails', suppressCmlDripEmails);
          }}
        />
        <EditCheckboxField
          checked={value.temporarilySuppressAllEmails}
          label="Temporarily Suppress All Emails"
          onCheckedChange={temporarilySuppressAllEmails => {
            updateField(
              'temporarilySuppressAllEmails',
              temporarilySuppressAllEmails
            );
          }}
        />
        {!value.temporarilySuppressAllEmails && (
          <>
            <EditFormField
              label="Date Temp Suppress was unchecked"
              onChange={dateTempSuppressUnchecked => {
                updateField(
                  'dateTempSuppressUnchecked',
                  dateTempSuppressUnchecked
                );
              }}
              type="date"
              value={toDateInputValue(value.dateTempSuppressUnchecked)}
            />
            <EditFormField
              label="Reason for unchecked Temp Suppress"
              onChange={reasonForUncheckedTempSuppress => {
                updateField(
                  'reasonForUncheckedTempSuppress',
                  reasonForUncheckedTempSuppress
                );
              }}
              required={
                initialValue.temporarilySuppressAllEmails &&
                !value.temporarilySuppressAllEmails
              }
              value={value.reasonForUncheckedTempSuppress || ''}
            />
          </>
        )}
        <EditCheckboxField
          checked={value.receiveInvestorCommunications}
          label="Receive Investor Communications"
          onCheckedChange={receiveInvestorCommunications => {
            updateField(
              'receiveInvestorCommunications',
              receiveInvestorCommunications
            );
          }}
        />
        <EditFormField
          label="Unsubscribe Date"
          onChange={unsubscribeDate => {
            updateField('unsubscribeDate', unsubscribeDate);
          }}
          type="date"
          value={toDateInputValue(value.unsubscribeDate)}
        />
        <EditFormField
          label="Unsubscribed By"
          onChange={unsubscribedBy => {
            updateField('unsubscribedBy', unsubscribedBy);
          }}
          value={value.unsubscribedBy}
        />
      </div>
    </EditDialog>
  );
}
